import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Table, Form, Badge, Spinner, Alert, Modal } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { eventService } from '../services/api';
import EventForm from './EventForm';
import './EventsManagement.css';

function EventsManagement() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [currentEvent, setCurrentEvent] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [timeFilter, setTimeFilter] = useState('upcoming');
  const [categories, setCategories] = useState([]);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    date: '',
    time: '',
    location: '',
    category: '',
    price: '',
    capacity: '',
    image: null,
    is_active: true,
    is_featured: false
  });

  // Load events and categories on component mount
  useEffect(() => {
    fetchEvents();
    fetchCategories();
  }, []);

  // Fetch all events
  const fetchEvents = async () => {
    setLoading(true);
    try {
      const response = await eventService.getAllEvents();
      // Process events to ensure they have reservation count properties
      const processedEvents = response.data.map(event => ({
        ...event,
        reservation_count: event.reservation_count || 0,
        confirmed_reservation_count: event.confirmed_reservation_count || 0
      }));
      setEvents(processedEvents);
      setError(null);
    } catch (err) {
      setError('Erreur lors du chargement des événements. Veuillez réessayer.');
      console.error('Error fetching events:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch categories
  const fetchCategories = async () => {
    try {
      const response = await eventService.getEventCategories();
      setCategories(response.data);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    
    if (type === 'file') {
      setFormData({
        ...formData,
        [name]: files[0]
      });
    } else if (type === 'checkbox') {
      setFormData({
        ...formData,
        [name]: checked
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  // Open add event modal - modified to navigate
  const handleAddEvent = () => {
    console.log('Navigating to add event form');
    navigate('/cpanel/events/add');
  };

  // Open edit event modal
  const handleEditEvent = (event) => {
    // Format date and time for form inputs
    let formattedDate = '';
    let formattedTime = '';
    
    try {
      // Safely parse the date
      const eventDate = new Date(event.date_time || event.date);
      
      // Check if the date is valid before formatting
      if (!isNaN(eventDate.getTime())) {
        formattedDate = eventDate.toISOString().split('T')[0];
        formattedTime = eventDate.toTimeString().split(' ')[0].substring(0, 5);
      } else {
        console.error('Invalid date:', event.date_time || event.date);
        // Set default values or current date as fallback
        const now = new Date();
        formattedDate = now.toISOString().split('T')[0];
        formattedTime = '12:00';
      }
    } catch (err) {
      console.error('Error formatting date:', err);
      // Set default values as fallback
      const now = new Date();
      formattedDate = now.toISOString().split('T')[0];
      formattedTime = '12:00';
    }

    setFormData({
      title: event.title,
      description: event.description,
      date: formattedDate,
      time: formattedTime,
      location: event.location,
      wilaya: event.wilaya,
      wilaya_name: event.wilaya_name,
      category: event.category?.id || '',
      price: event.price,
      capacity: event.available_seats,
      is_active: event.is_active,
      is_featured: event.is_featured,
      image: null // Don't pre-fill image
    });
    setCurrentEvent(event);
    setShowEditModal(true);
  };

  // Open delete confirmation modal
  const handleDeleteConfirm = (event) => {
    setCurrentEvent(event);
    setShowDeleteModal(true);
  };

  // Open event details modal
  const handleViewDetails = (event) => {
    setCurrentEvent(event);
    setShowDetailsModal(true);
  };

  // Submit form to create new event
  const handleCreateEvent = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const eventData = new FormData();
      
      // Validate required fields
      if (!formData.title || !formData.date || !formData.time || !formData.location) {
        throw new Error('Veuillez remplir tous les champs obligatoires');
      }
      
      // Append form data
      eventData.append('title', formData.title);
      eventData.append('description', formData.description);
      eventData.append('date', formData.date);
      eventData.append('time', formData.time);
      eventData.append('location', formData.location);
      eventData.append('wilaya', formData.wilaya || '16');
      
      if (formData.category) {
        eventData.append('category', formData.category);
      }
      
      if (formData.price) {
        eventData.append('price', formData.price);
      }
      
      if (formData.capacity) {
        eventData.append('available_seats', formData.capacity);
      }
      
      eventData.append('is_active', formData.is_active);
      eventData.append('is_featured', formData.is_featured);
      
      if (formData.image) {
        eventData.append('image', formData.image);
      }
      
      await eventService.createEvent(eventData);
      
      // Close modal and refresh events list
      setShowAddModal(false);
      await fetchEvents();
      setError(null);
      
    } catch (err) {
      setError(err.message || 'Erreur lors de la création de l\'événement. Veuillez réessayer.');
      console.error('Error creating event:', err);
    } finally {
      setLoading(false);
    }
  };

  // Submit form to update event
  const handleUpdateEvent = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Create FormData object for file upload
      const eventData = new FormData();
      eventData.append('title', formData.title);
      eventData.append('description', formData.description);
      
      // Combine date and time
      const dateTime = `${formData.date}T${formData.time}:00`;
      eventData.append('date_time', dateTime);
      
      eventData.append('location', formData.location);
      eventData.append('wilaya', formData.wilaya);
      eventData.append('wilaya_name', formData.wilaya_name);
      eventData.append('category', formData.category);
      eventData.append('price', formData.price);
      eventData.append('capacity', formData.capacity);
      eventData.append('is_active', formData.is_active);
      eventData.append('is_featured', formData.is_featured);
      
      if (formData.image) {
        eventData.append('image', formData.image);
      }
      
      await eventService.updateEvent(currentEvent.id, eventData);
      setShowEditModal(false);
      fetchEvents(); // Refresh events list
      setError(null);
    } catch (err) {
      setError('Erreur lors de la mise à jour de l\'événement. Veuillez réessayer.');
      console.error('Error updating event:', err);
    } finally {
      setLoading(false);
    }
  };

  // Delete event
  const handleDeleteEvent = async () => {
    setLoading(true);
    
    try {
      await eventService.deleteEvent(currentEvent.id);
      setShowDeleteModal(false);
      fetchEvents(); // Refresh events list
      setError(null);
    } catch (err) {
      setError('Erreur lors de la suppression de l\'événement. Vérifiez qu\'il n\'y a pas de réservations associées.');
      console.error('Error deleting event:', err);
      setShowDeleteModal(false);
    } finally {
      setLoading(false);
    }
  };

  // Toggle event active status
  const handleToggleStatus = async (event) => {
    setLoading(true);
    
    try {
      await eventService.updateEvent(event.id, {
        ...event,
        is_active: !event.is_active
      });
      fetchEvents(); // Refresh events list
      setError(null);
    } catch (err) {
      setError('Erreur lors de la mise à jour du statut. Veuillez réessayer.');
      console.error('Error updating event status:', err);
    } finally {
      setLoading(false);
    }
  };

  // Filter events based on search term and filters
  const filteredEvents = events.filter(event => {
    // Search term filter
    const matchesSearch = event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          event.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          event.wilaya_name.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Status filter
    const matchesStatus = statusFilter === 'all' || 
                          (statusFilter === 'active' && event.is_active) ||
                          (statusFilter === 'inactive' && !event.is_active);
    
    // Category filter
    const matchesCategory = categoryFilter === 'all' || 
                            (event.category && event.category.id.toString() === categoryFilter);
    
    // Time filter
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const eventDate = new Date(event.date);
    eventDate.setHours(0, 0, 0, 0);
    
    const matchesTime = timeFilter === 'all' || 
                        (timeFilter === 'upcoming' && eventDate >= today) ||
                        (timeFilter === 'past' && eventDate < today);
    
    return matchesSearch && matchesStatus && matchesCategory && matchesTime;
  });

  return (
    <Container fluid className="events-management py-4">
      {error && <Alert variant="danger">{error}</Alert>}
      
      <Card className="mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Gestion des événements</h5>
          <Button 
            variant="primary" 
            onClick={handleAddEvent}
            disabled={loading}
          >
            {loading ? (
              <Spinner as="span" animation="border" size="sm" className="me-2" />
            ) : (
              <i className="fas fa-plus me-2"></i>
            )}
            Ajouter un événement
          </Button>
        </Card.Header>
        <Card.Body>
          {/* Filters */}
          <Row className="mb-4">
            <Col md={3}>
              <Form.Group>
                <Form.Control 
                  type="text" 
                  placeholder="Rechercher un événement..." 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Select 
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">Tous les statuts</option>
                <option value="active">Actifs</option>
                <option value="inactive">Inactifs</option>
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Select 
                value={timeFilter}
                onChange={(e) => setTimeFilter(e.target.value)}
              >
                <option value="all">Toutes les dates</option>
                <option value="upcoming">À venir</option>
                <option value="past">Passés</option>
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Select 
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <option value="all">Toutes les catégories</option>
                {categories.map(category => (
                  <option key={category.id} value={category.id.toString()}>
                    {category.name}
                  </option>
                ))}
              </Form.Select>
            </Col>
          </Row>
          
          {/* Events Table */}
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : filteredEvents.length === 0 ? (
            <div className="text-center py-5">
              <p className="text-muted">Aucun événement trouvé</p>
            </div>
          ) : (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Titre</th>
                  <th>Date</th>
                  <th>Lieu</th>
                  <th>Catégorie</th>
                  <th>Prix</th>
                  <th>Places</th>
                  <th>Réservations</th>
                  <th>Confirmées</th>
                  <th>Statut</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredEvents.map(event => (
                  <tr key={event.id}>
                    <td>{event.title}</td>
                    <td>
                      {(() => {
                        try {
                          return new Date(event.date).toLocaleDateString();
                        } catch (e) {
                          return 'Invalid date';
                        }
                      })()}
                    </td>
                    <td>{event.wilaya}-{event.wilaya_name}</td>
                    <td>{event.category?.name || 'Non catégorisé'}</td>
                    <td>{event.price || 'Gratuit'}</td>
                    <td>{event.available_seats || 'N/A'}</td>
                    <td>
                      {event.reservation_count || 0}
                      {event.available_seats && 
                        <span className="text-muted"> / {event.available_seats}</span>
                      }
                    </td>
                    <td>{event.confirmed_reservation_count || 0}</td>
                    <td>
                      {event.is_active ? (
                        <Badge bg="success">Actif</Badge>
                      ) : (
                        <Badge bg="secondary">Inactif</Badge>
                      )}
                    </td>
                    <td>
                      <Button 
                        variant="outline-info" 
                        size="sm" 
                        className="me-1"
                        onClick={() => handleViewDetails(event)}
                      >
                        <i className="fas fa-eye"></i>
                      </Button>
                      <Button 
                        variant="outline-primary" 
                        size="sm" 
                        className="me-1"
                        onClick={() => handleEditEvent(event)}
                      >
                        <i className="fas fa-edit"></i>
                      </Button>
                      <Button 
                        variant={event.is_active ? "outline-warning" : "outline-success"} 
                        size="sm" 
                        className="me-1"
                        onClick={() => handleToggleStatus(event)}
                      >
                        {event.is_active ? (
                          <i className="fas fa-ban"></i>
                        ) : (
                          <i className="fas fa-check"></i>
                        )}
                      </Button>
                      <Button 
                        variant="outline-danger" 
                        size="sm"
                        onClick={() => handleDeleteConfirm(event)}
                      >
                        <i className="fas fa-trash"></i>
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
      
           {/* Edit Event Modal */}
           <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Modifier l'événement</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleUpdateEvent}>
          <Modal.Body>
            {/* Form content */}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEditModal(false)}>
              Annuler
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Spinner as="span" animation="border" size="sm" className="me-2" />
                  Mise à jour...
                </>
              ) : (
                'Mettre à jour'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      
 
      </Modal>
      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirmer la suppression</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Êtes-vous sûr de vouloir supprimer l'événement <strong>{currentEvent?.title}</strong> ?</p>
          <Alert variant="warning">
            <i className="fas fa-exclamation-triangle me-2"></i>
            Cette action est irréversible. La suppression n'est possible que s'il n'y a pas de réservations associées à cet événement.
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Annuler
          </Button>
          <Button variant="danger" onClick={handleDeleteEvent} disabled={loading}>
            {loading ? (
              <>
                <Spinner as="span" animation="border" size="sm" className="me-2" />
                Suppression...
              </>
            ) : (
              'Supprimer'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
      
      {/* Event Details Modal */}
      <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Détails de l'événement</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {currentEvent && (
            <Row>
              <Col md={6}>
                <div className="event-image-container mb-3">
                  {currentEvent.image ? (
                    <img 
                      src={currentEvent.image} 
                      alt={currentEvent.title} 
                      className="img-fluid rounded"
                    />
                  ) : (
                    <div className="no-image-placeholder">
                      <i className="fas fa-image"></i>
                      <p>Pas d'image</p>
                    </div>
                  )}
                </div>
              </Col>
              <Col md={6}>
                <h4>{currentEvent.title}</h4>
                <p className="text-muted">
                  <i className="fas fa-calendar me-2"></i>
                  {new Date(currentEvent.date_time).toLocaleDateString()} à {new Date(currentEvent.date_time).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                </p>
                <p className="text-muted">
                  <i className="fas fa-map-marker-alt me-2"></i>
                  {currentEvent.wilaya_name}
                </p>
                <p className="text-muted">
                  <i className="fas fa-tag me-2"></i>
                  {currentEvent.category?.name || 'Non catégorisé'}
                </p>
                <div className="d-flex mb-3">
                  <div className="me-3">
                    <Badge bg="info">
                      <i className="fas fa-euro-sign me-1"></i>
                      {currentEvent.price} €
                    </Badge>
                  </div>
                  <div className="me-3">
                    <Badge bg="secondary">
                      <i className="fas fa-users me-1"></i>
                      {currentEvent.capacity} places
                    </Badge>
                  </div>
                  <div>
                    {currentEvent.is_active ? (
                      <Badge bg="success">Actif</Badge>
                    ) : (
                      <Badge bg="danger">Inactif</Badge>
                    )}
                  </div>
                </div>
                {currentEvent.is_featured && (
                  <div className="mb-3">
                    <Badge bg="warning" text="dark">
                      <i className="fas fa-star me-1"></i>
                      En vedette
                    </Badge>
                  </div>
                )}
              </Col>
              <Col xs={12} className="mt-3">
                <h5>Description</h5>
                <p>{currentEvent.description}</p>
              </Col>
              <Col xs={12} className="mt-3">
                <h5>Réservations</h5>
                <Button 
                  variant="outline-primary" 
                  size="sm"
                  as={Link}
                  to={`/cpanel/events/${currentEvent.id}/reservations`}
                  onClick={() => setShowDetailsModal(false)}
                >
                  <i className="fas fa-ticket-alt me-2"></i>
                  Voir les réservations
                </Button>
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailsModal(false)}>
            Fermer
          </Button>
          <Button 
            variant="primary" 
            onClick={() => {
              setShowDetailsModal(false);
              handleEditEvent(currentEvent);
            }}
          >
            <i className="fas fa-edit me-2"></i>
            Modifier
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default EventsManagement;